import { useEffect, useRef } from 'react';

// Lightweight canvas confetti — no dependency. Deterministic via `seed`.
export default function Confetti({ seed = 'wildquest', duration = 3200, count = 90 }) {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const dpr = window.devicePixelRatio || 1;
    const resize = () => {
      canvas.width = window.innerWidth * dpr;
      canvas.height = window.innerHeight * dpr;
      canvas.style.width = window.innerWidth + 'px';
      canvas.style.height = window.innerHeight + 'px';
    };
    resize();
    window.addEventListener('resize', resize);

    // Seed a tiny PRNG
    let h = 0;
    for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
    const rand = () => {
      h = (h * 1664525 + 1013904223) >>> 0;
      return h / 0xffffffff;
    };
    const colors = ['#E9C46A', '#FF6EC7', '#7DF9FF', '#95D5B2', '#F4A261', '#D4A5DE', '#8ECAE6'];
    const pieces = Array.from({ length: count }, () => ({
      x: rand() * canvas.width,
      y: -20 * dpr - rand() * canvas.height * 0.5,
      vx: (rand() - 0.5) * 4 * dpr,
      vy: (2 + rand() * 3) * dpr,
      w: (5 + rand() * 6) * dpr,
      h: (8 + rand() * 8) * dpr,
      rot: rand() * Math.PI * 2,
      vr: (rand() - 0.5) * 0.35,
      color: colors[Math.floor(rand() * colors.length)],
      shape: rand() > 0.5 ? 'rect' : 'circle',
    }));

    const start = performance.now();
    let raf;
    const tick = (t) => {
      const elapsed = t - start;
      const gone = elapsed >= duration;
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      pieces.forEach(p => {
        p.x += p.vx;
        p.y += p.vy;
        p.vy += 0.05 * dpr;   // gravity
        p.rot += p.vr;
        ctx.save();
        ctx.translate(p.x, p.y);
        ctx.rotate(p.rot);
        ctx.fillStyle = p.color;
        ctx.globalAlpha = gone ? Math.max(0, 1 - (elapsed - duration) / 800) : 1;
        if (p.shape === 'rect') ctx.fillRect(-p.w / 2, -p.h / 2, p.w, p.h);
        else { ctx.beginPath(); ctx.arc(0, 0, p.w / 2, 0, Math.PI * 2); ctx.fill(); }
        ctx.restore();
      });
      if (elapsed < duration + 800) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => { cancelAnimationFrame(raf); window.removeEventListener('resize', resize); };
  }, [seed, duration, count]);

  return (
    <canvas
      ref={canvasRef}
      className="pointer-events-none fixed inset-0 z-[110]"
      aria-hidden
      data-testid="confetti-canvas"
    />
  );
}
